import { useState } from 'react';
import { Card, Badge, Modal, SlidePanel, PanelSection, SpecGrid, SpecCell, toast } from '@/components';
import { useApi } from '@/api/hooks';
import { api } from '@/api/client';
import { useRole } from '@/hooks/useRole';
import styles from './Support.module.css';

interface Reply {
  id: number;
  author: string;
  body: string;
  created_at: string;
}

interface Ticket {
  id: number;
  tenant_id: number | string;
  tenant_name: string;
  subject: string;
  body: string;
  status: string;
  priority: string;
  assignee: string | null;
  category: string | null;
  contact_name: string | null;
  admin_note: string | null;
  replies: Reply[];
  created_at: string;
  updated_at: string;
}

interface Tenant {
  id: string;
  company_name: string;
}

const categoryLabel: Record<string, string> = {
  portal: 'ポータル入稿', contract: '書類生成', billing: '課金・請求',
  property: '物件管理', auth: '認証・ログイン', crm: '顧客CRM',
  minpaku: '民泊管理', other: 'その他',
};
const priorityVariant: Record<string, 'red' | 'amber' | 'blue' | 'gray'> = {
  urgent: 'red', high: 'red', medium: 'blue', low: 'gray',
};
const priorityLabel: Record<string, string> = {
  urgent: '緊急', high: '高優先度', medium: '中優先度', low: '低優先度',
};
const statusVariant: Record<string, 'red' | 'amber' | 'green' | 'blue' | 'purple' | 'gray'> = {
  open: 'blue', in_progress: 'amber', waiting: 'purple', resolved: 'green', closed: 'gray',
};
const statusLabel: Record<string, string> = {
  open: '新規', in_progress: '対応中', waiting: '保留', resolved: '解決済', closed: 'クローズ',
};

export function Support() {
  const { isAdmin } = useRole();
  const { data: tickets, loading, refetch } = useApi<Ticket[]>('/console/tickets');
  const { data: tenants } = useApi<Tenant[]>('/console/tenants');

  const [priorityFilter, setPriorityFilter] = useState('全優先度');
  const [statusFilter, setStatusFilter] = useState('全ステータス');
  const [selected, setSelected] = useState<Ticket | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);

  // 詳細パネルの編集状態
  const [editStatus, setEditStatus] = useState('');
  const [editAssignee, setEditAssignee] = useState('');
  const [editNote, setEditNote] = useState('');
  const [replying, setReplying] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [saving, setSaving] = useState(false);

  // 作成フォーム
  const [form, setForm] = useState({
    tenant_id: '', priority: 'medium', title: '', assignee: '', category: '', contact_name: '',
  });

  const filtered = (tickets ?? []).filter((t) => {
    if (priorityFilter !== '全優先度' && (priorityLabel[t.priority] ?? t.priority) !== priorityFilter) return false;
    if (statusFilter !== '全ステータス' && (statusLabel[t.status] ?? t.status) !== statusFilter) return false;
    return true;
  });

  const openDetail = (t: Ticket) => {
    setSelected(t);
    setEditStatus(t.status);
    setEditAssignee(t.assignee ?? '');
    setEditNote('');
    setReplyText('');
    setReplying(false);
  };

  const handleSave = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      const updated = await api<Ticket>(`/console/tickets/${selected.id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          status: editStatus,
          assignee: editAssignee || null,
          notes: editNote || undefined,
        }),
      });
      setSelected(updated);
      toast('チケットを更新しました', 'success');
      setEditNote('');
      refetch();
    } catch { toast('更新に失敗しました', 'error'); }
    finally { setSaving(false); }
  };

  const handleResolve = async () => {
    if (!selected) return;
    try {
      const updated = await api<Ticket>(`/console/tickets/${selected.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ status: 'resolved' }),
      });
      setSelected(updated);
      setEditStatus('resolved');
      toast('チケットを解決済みにしました', 'success');
      refetch();
    } catch { toast('更新に失敗しました', 'error'); }
  };

  const handleEscalate = async () => {
    if (!selected) return;
    try {
      await api(`/console/tickets/${selected.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ priority: 'urgent', status: 'in_progress' }),
      });
      toast('チケットをエスカレーションしました', 'success');
      refetch();
    } catch { toast('エスカレーションに失敗しました', 'error'); }
  };

  const handleReply = async () => {
    if (!selected || !replyText.trim()) { toast('返信内容を入力してください', 'error'); return; }
    try {
      const res = await api<{ message: string; sentTo: string[] }>(
        `/console/tickets/${selected.id}/reply`,
        { method: 'POST', body: JSON.stringify({ message: replyText }) },
      );
      toast(`${res.sentTo?.join(', ') || 'テナント'} に返信しました`, 'success');
      setReplyText('');
      setReplying(false);
      refetch();
    } catch { toast('返信に失敗しました', 'error'); }
  };

  const handleCreate = async () => {
    if (!form.tenant_id || !form.title) { toast('テナントと件名は必須です', 'error'); return; }
    try {
      await api('/console/tickets', { method: 'POST', body: JSON.stringify(form) });
      toast('チケットを作成しました', 'success');
      setShowCreateModal(false);
      setForm({ tenant_id: '', priority: 'medium', title: '', assignee: '', category: '', contact_name: '' });
      refetch();
    } catch { toast('チケット作成に失敗しました', 'error'); }
  };

  if (loading) return <div className={styles.page}>読み込み中...</div>;

  return (
    <div className={styles.page}>
      {/* ツールバー */}
      <div className={styles.toolbar}>
        <select className={styles.filterSelect} value={priorityFilter} onChange={(e) => setPriorityFilter(e.target.value)}>
          <option>全優先度</option>
          <option>高優先度</option>
          <option>中優先度</option>
          <option>低優先度</option>
        </select>
        <select className={styles.filterSelect} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option>全ステータス</option>
          <option>新規</option>
          <option>対応中</option>
          <option>保留</option>
          <option>解決済</option>
          <option>クローズ</option>
        </select>
        <div style={{ flex: 1 }} />
        <button className={styles.btnPrimary} onClick={() => setShowCreateModal(true)}>＋ チケット作成</button>
      </div>

      {/* チケット一覧 */}
      <Card title={`サポートチケット（${filtered.length}件）`}>
        <div className={styles.ticketList}>
          {filtered.map((t) => (
            <div key={t.id} className={styles.ticketRow} onClick={() => openDetail(t)}>
              <div className={styles.ticketLeft}>
                <span className={styles.ticketId}>#{String(t.id).padStart(4, '0')}</span>
                <div>
                  <div className={styles.ticketTitle}>{t.subject}</div>
                  <div className={styles.ticketMeta}>
                    {t.tenant_name}
                    {t.category ? ` ｜ ${categoryLabel[t.category] ?? t.category}` : ''}
                    {t.contact_name ? ` ｜ ${t.contact_name}` : t.assignee ? ` ｜ ${t.assignee}` : ''}
                  </div>
                </div>
              </div>
              <div className={styles.ticketRight}>
                <Badge variant={priorityVariant[t.priority] ?? 'gray'}>{priorityLabel[t.priority] ?? t.priority}</Badge>
                <Badge variant={statusVariant[t.status] ?? 'gray'}>{statusLabel[t.status] ?? t.status}</Badge>
                <span className={styles.ticketTime}>
                  {new Date(t.created_at).toLocaleDateString('ja-JP', { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>
            </div>
          ))}
          {filtered.length === 0 && <div className={styles.empty}>チケットはありません</div>}
        </div>
      </Card>

      {/* チケット詳細パネル */}
      <SlidePanel
        open={!!selected}
        onClose={() => setSelected(null)}
        title={selected ? `#${String(selected.id).padStart(4, '0')} — ${selected.subject}` : ''}
        width={700}
        actions={selected ? (
          <>
            {isAdmin && <button className={styles.btnEscalate} onClick={handleEscalate}>🔺 エスカレ</button>}
            {isAdmin && <button className={styles.btnResolve} onClick={handleResolve}
              disabled={selected.status === 'resolved' || selected.status === 'closed'}>
              ✅ 解決
            </button>}
          </>
        ) : undefined}
      >
        {selected && (
          <>
            {/* ① チケット情報 */}
            <PanelSection>
              <SpecGrid cols={3}>
                <SpecCell label="テナント" value={selected.tenant_name} />
                <SpecCell label="優先度">
                  <Badge variant={priorityVariant[selected.priority] ?? 'gray'}>
                    {priorityLabel[selected.priority] ?? selected.priority}
                  </Badge>
                </SpecCell>
                <SpecCell label="ステータス">
                  <Badge variant={statusVariant[selected.status] ?? 'gray'}>
                    {statusLabel[selected.status] ?? selected.status}
                  </Badge>
                </SpecCell>
                <SpecCell label="カテゴリ" value={selected.category ? (categoryLabel[selected.category] ?? selected.category) : '—'} />
                <SpecCell label="報告者" value={selected.contact_name ?? selected.assignee ?? '—'} />
                <SpecCell label="受付日時" value={new Date(selected.created_at).toLocaleString('ja-JP')} mono />
              </SpecGrid>
            </PanelSection>

            {/* ② 問い合わせ内容 */}
            <PanelSection title="問い合わせ内容">
              <div className={styles.bodyBox}>{selected.body ?? '—'}</div>
            </PanelSection>

            {/* ③ 返信履歴 */}
            {(selected.replies ?? []).length > 0 && (
              <PanelSection title="返信履歴">
                <div className={styles.replyList}>
                  {(selected.replies ?? []).map((r) => (
                    <div key={r.id} className={styles.replyItem}>
                      <div className={styles.replyHeader}>
                        <span className={styles.replyAuthor}>{r.author}</span>
                        <span className={styles.replyTime}>
                          {new Date(r.created_at).toLocaleString('ja-JP')}
                        </span>
                      </div>
                      <div className={styles.replyBody}>{r.body}</div>
                    </div>
                  ))}
                </div>
              </PanelSection>
            )}

            {/* ④ 対応メモ・ステータス更新 */}
            <PanelSection title="対応">
              {/* ステータス・担当者 */}
              <div className={styles.editRow}>
                <div className={styles.editField}>
                  <label className={styles.formLabel}>ステータス</label>
                  <select className={styles.formSelect} value={editStatus} onChange={(e) => setEditStatus(e.target.value)}>
                    <option value="open">新規</option>
                    <option value="in_progress">対応中</option>
                    <option value="waiting">保留</option>
                    <option value="resolved">解決済</option>
                    <option value="closed">クローズ</option>
                  </select>
                </div>
                <div className={styles.editField}>
                  <label className={styles.formLabel}>担当者</label>
                  <input
                    className={styles.formInput}
                    type="text"
                    value={editAssignee}
                    onChange={(e) => setEditAssignee(e.target.value)}
                    placeholder="担当者名"
                  />
                </div>
              </div>

              {/* 既存メモ */}
              {selected.admin_note && (
                <div className={styles.adminNote}>{selected.admin_note}</div>
              )}

              {/* 新規メモ */}
              <textarea
                className={styles.noteArea}
                placeholder="対応メモを追記…"
                rows={3}
                value={editNote}
                onChange={(e) => setEditNote(e.target.value)}
              />

              {/* テナント返信エリア */}
              {isAdmin && replying && (
                <div className={styles.replyBox}>
                  <label className={styles.formLabel}>テナントへの返信内容</label>
                  <textarea
                    className={styles.noteArea}
                    placeholder="返信内容を入力…"
                    rows={3}
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                  />
                  <div className={styles.replyBoxActions}>
                    <button className={styles.btnGhost} onClick={() => { setReplying(false); setReplyText(''); }}>キャンセル</button>
                    <button className={styles.btnPrimary} onClick={handleReply}>送信する</button>
                  </div>
                </div>
              )}

              <div className={styles.panelActions}>
                {isAdmin && !replying && (
                  <button className={styles.btnSecondary} onClick={() => setReplying(true)}>
                    📧 テナントに返信
                  </button>
                )}
                {isAdmin && <button className={styles.btnPrimary} onClick={handleSave} disabled={saving}>
                  {saving ? '保存中…' : '保存'}
                </button>}
              </div>
            </PanelSection>
          </>
        )}
      </SlidePanel>

      {/* チケット作成モーダル */}
      <Modal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="🎧 サポートチケット作成"
        width={540}
        footer={
          <>
            <button className={styles.btnGhost} onClick={() => setShowCreateModal(false)}>キャンセル</button>
            <button className={styles.btnPrimary} onClick={handleCreate}>チケットを作成</button>
          </>
        }
      >
        <div className={styles.formGroup}>
          <label className={styles.formLabel}>対象テナント</label>
          <select className={styles.formSelect} value={form.tenant_id} onChange={(e) => setForm({ ...form, tenant_id: e.target.value })}>
            <option value="">テナントを選択...</option>
            {(tenants ?? []).map((t) => <option key={t.id} value={t.id}>{t.company_name}</option>)}
          </select>
        </div>
        <div className={styles.formGroup}>
          <label className={styles.formLabel}>優先度</label>
          <select className={styles.formSelect} value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })}>
            <option value="low">低</option>
            <option value="medium">中</option>
            <option value="high">高</option>
          </select>
        </div>
        <div className={styles.formGroup}>
          <label className={styles.formLabel}>カテゴリ</label>
          <select className={styles.formSelect} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
            <option value="">選択してください</option>
            {Object.entries(categoryLabel).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </div>
        <div className={styles.formGroup}>
          <label className={styles.formLabel}>件名</label>
          <input className={styles.formInput} type="text" placeholder="例：HOME'S への入稿でエラーが発生する" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
        </div>
        <div className={styles.formGroup}>
          <label className={styles.formLabel}>連絡先担当者</label>
          <input className={styles.formInput} type="text" placeholder="例：田村 誠司" value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} />
        </div>
      </Modal>
    </div>
  );
}
